import { useEffect, useMemo, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, CircleMarker, Tooltip, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../api/client.js';

const BRAZIL_CENTER = [-14.235, -51.925];

const CUSTOMER_STYLE = {
  color: '#0ea5e9',
  weight: 2,
  fillColor: '#38bdf8',
  fillOpacity: 0.85,
};

const DELIVERY_STYLE = {
  color: '#ea580c',
  weight: 2,
  fillColor: '#fb923c',
  fillOpacity: 0.9,
};

/** @typedef {'all' | 'customers' | 'delivery'} MapLayerFilter */

/** Impede que a roda do mouse propague para a página; o zoom fica no mapa. */
function MapScrollIsolation() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    L.DomEvent.disableScrollPropagation(el);
    map.scrollWheelZoom.enable();
    const t = window.setTimeout(() => map.invalidateSize(), 80);
    return () => window.clearTimeout(t);
  }, [map]);
  return null;
}

/** Ajusta o zoom/enquadramento aos pontos visíveis. */
function FitToPoints({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].latitude, points[0].longitude], 14);
      return;
    }
    const bounds = points.map((p) => [p.latitude, p.longitude]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

function hasCoords(item) {
  return Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude));
}

export function CustomersMapModal({ tenantId, onClose }) {
  const [customerPoints, setCustomerPoints] = useState([]);
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [pending, setPending] = useState(0);
  const [pendingDelivery, setPendingDelivery] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** @type {[MapLayerFilter, function(MapLayerFilter): void]} */
  const [layerFilter, setLayerFilter] = useState('all');

  async function load() {
    if (!tenantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api(`/tenants/${tenantId}/customers/map`);
      const customers = Array.isArray(data.customers) ? data.customers : [];
      const deliveries = Array.isArray(data.delivery_addresses) ? data.delivery_addresses : [];
      setCustomerPoints(customers.filter(hasCoords));
      setDeliveryPoints(deliveries.filter(hasCoords));
      setPending(Number(data.pending) || 0);
      setPendingDelivery(Number(data.pending_delivery) || 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar o mapa.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Trava o scroll da página enquanto o modal está aberto.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarW > 0) {
      document.body.style.paddingRight = `${scrollbarW}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, []);

  const showCustomers = layerFilter === 'all' || layerFilter === 'customers';
  const showDelivery = layerFilter === 'all' || layerFilter === 'delivery';

  const visiblePoints = useMemo(() => {
    const out = [];
    if (showCustomers) {
      out.push(...customerPoints);
    }
    if (showDelivery) {
      out.push(...deliveryPoints);
    }
    return out;
  }, [customerPoints, deliveryPoints, showCustomers, showDelivery]);

  const center = useMemo(() => {
    if (!visiblePoints.length) return BRAZIL_CENTER;
    return [visiblePoints[0].latitude, visiblePoints[0].longitude];
  }, [visiblePoints]);

  const hasPending = pending > 0 || pendingDelivery > 0;
  const totalVisible = (showCustomers ? customerPoints.length : 0)
    + (showDelivery ? deliveryPoints.length : 0);

  const filterBtnClass = (active) =>
    `rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'bg-ink text-white dark:bg-white dark:text-ink'
        : 'border border-ink/10 hover:bg-ink/5 dark:border-white/10 dark:hover:bg-white/10'
    }`;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-2xl dark:border-white/10 dark:bg-zinc-950"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 px-5 py-3 dark:border-white/10">
          <div>
            <h3 className="font-display text-lg font-semibold">Mapa de clientes</h3>
            <p className="text-xs text-ink/50 dark:text-frost-muted">
              {customerPoints.length} cadastro{customerPoints.length === 1 ? '' : 's'}
              {' · '}
              {deliveryPoints.length} entrega{deliveryPoints.length === 1 ? '' : 's'}
              {hasPending
                ? ` · ${pending + pendingDelivery} ainda sendo localizado${pending + pendingDelivery === 1 ? '' : 's'}`
                : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border border-ink/10 p-0.5 dark:border-white/10">
              <button type="button" className={filterBtnClass(layerFilter === 'all')} onClick={() => setLayerFilter('all')}>
                Todos
              </button>
              <button type="button" className={filterBtnClass(layerFilter === 'customers')} onClick={() => setLayerFilter('customers')}>
                Cadastro
              </button>
              <button type="button" className={filterBtnClass(layerFilter === 'delivery')} onClick={() => setLayerFilter('delivery')}>
                Entregas
              </button>
            </div>
            {hasPending && (
              <button
                type="button"
                onClick={() => load().catch(() => {})}
                disabled={loading}
                className="rounded-full border border-ink/10 px-3 py-1.5 text-xs font-medium hover:bg-ink/5 disabled:opacity-50 dark:border-white/10 dark:hover:bg-white/10"
              >
                {loading ? 'Atualizando...' : 'Localizar mais'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="grid h-9 w-9 place-items-center rounded-full border border-ink/10 text-lg leading-none hover:bg-ink/5 dark:border-white/10 dark:hover:bg-white/10"
            >
              ×
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          {/* Mapa em camada de baixo — recebe todos os cliques/arrastes */}
          <MapContainer
            center={center}
            zoom={visiblePoints.length ? 12 : 4}
            scrollWheelZoom
            dragging
            doubleClickZoom
            zoomControl={false}
            className="absolute inset-0 z-0 h-full w-full"
            style={{ background: '#e8eef2' }}
          >
            <MapScrollIsolation />
            <ZoomControl position="topright" />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitToPoints points={visiblePoints} />
            {showDelivery &&
              deliveryPoints.map((a) => (
                <CircleMarker
                  key={`d-${a.id}`}
                  center={[a.latitude, a.longitude]}
                  radius={7}
                  pathOptions={DELIVERY_STYLE}
                >
                  <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                    <span className="font-semibold">{a.nome}</span>
                    {a.label ? (
                      <>
                        <br />
                        <span style={{ fontSize: '11px' }}>{a.label}</span>
                      </>
                    ) : null}
                    {a.endereco ? (
                      <>
                        <br />
                        <span style={{ fontSize: '11px', opacity: 0.75 }}>{a.endereco}</span>
                      </>
                    ) : null}
                  </Tooltip>
                </CircleMarker>
              ))}
            {showCustomers &&
              customerPoints.map((c) => (
                <CircleMarker
                  key={`c-${c.id}`}
                  center={[c.latitude, c.longitude]}
                  radius={10}
                  pathOptions={CUSTOMER_STYLE}
                >
                  <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                    <span className="font-semibold">{c.nome}</span>
                    <br />
                    <span style={{ fontSize: '10px', opacity: 0.65 }}>Morada principal</span>
                    {c.endereco ? (
                      <>
                        <br />
                        <span style={{ fontSize: '11px', opacity: 0.75 }}>{c.endereco}</span>
                      </>
                    ) : null}
                  </Tooltip>
                </CircleMarker>
              ))}
          </MapContainer>

          {/* UI por cima do mapa — sempre pointer-events-none para não bloquear interação */}
          {error && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 m-3 rounded-2xl bg-red-500/10 px-4 py-2 text-sm text-red-600 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="pointer-events-none absolute bottom-4 left-4 z-10 rounded-2xl border border-ink/10 bg-white/95 px-3 py-2 text-xs shadow-md dark:border-white/10 dark:bg-zinc-950/95">
            <div className="mb-1 font-semibold text-ink/70 dark:text-frost-muted">Legenda</div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-500 bg-sky-400" />
              <span>Morada principal (cadastro)</span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-orange-600 bg-orange-400" />
              <span>Morada de entrega</span>
            </div>
          </div>

          {!loading && totalVisible > 0 && (
            <div className="pointer-events-none absolute bottom-4 right-4 z-10 rounded-full bg-white/90 px-3 py-1 text-xs text-ink/60 shadow dark:bg-zinc-950/90 dark:text-frost-muted">
              {totalVisible} ponto{totalVisible === 1 ? '' : 's'} visíve{totalVisible === 1 ? 'l' : 'is'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
